// This is a sample store for the fact API (Testing purpose)

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Auth, AuthRequest } from '../../models/models';
import { ApiResponseStatus } from '../../models/enums';
import { request } from 'services/apis';
import { toast } from 'react-toastify';
import jwtDecode from 'jwt-decode';

const initialState: Auth = {
  user: {
    isAuthenticated: false,
    authData: {}
  },
  refreshToken: undefined,
  status: '',
  error: false,
  employeeId: '',
  employeeIdStatus: '',
  employeeIdError: false
};

export const getToken = createAsyncThunk(
  'auth/getToken',
  async (query: AuthRequest) => {
    try {
      const { token } = query;
      const response = await request.aadApiGet('/Auth/get-user-roles', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      console.log('Response from getToken:', response.data);
      const decoded: { upn?: string } = jwtDecode(token);

      return {
        data: response.data,
        token,
        upn: decoded.upn
      };
    } catch (error) {
      console.error(error);
      toast.error('Failed to login. Please try again.');
      throw error;
    }
  }
);

export const getEmployeeIdByUpn = createAsyncThunk(
  'auth/getEmployeeIdByUpn',
  async (_, { getState }) => {
    try {
      const state = getState() as any;
      const authData = state.auth.user.authData;

      if (authData?.data?.userRoleId) {
        return authData.data.userRoleId;
      }
      if (authData?.data?.employeeInfoId) {
        return authData.data.employeeInfoId;
      }

      if (authData?.token) {
        const decodedToken: any = jwtDecode(authData.token);
        console.log('Decoded JWT token:', decodedToken);

        const upn = decodedToken.upn || decodedToken.unique_name;
        if (upn) {
          console.log('UPN from token:', upn);

          const response = await request.get<string>(
            `/employees/employee-id/${upn}`
          );
          const employeeId = response.data;
          console.log('Employee ID from API:', employeeId);
          return employeeId;
        }

        return decodedToken.oid || decodedToken.sub || '';
      }

      throw new Error('No authentication data available');
    } catch (error) {
      console.error('Error fetching employee ID:', error);
      throw error;
    }
  }
);

const userSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthState: (state) => ({ ...state, ...initialState }),
    setRefreshToken: (state, action) => {
      state.refreshToken = action.payload;
    },
    clearEmployeeId: (state) => {
      state.employeeId = '';
      state.employeeIdStatus = '';
      state.employeeIdError = false;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(getToken.pending, (state) => {
      state.status = ApiResponseStatus.pending;
      state.error = false;
    });

    builder.addCase(getToken.fulfilled, (state, { payload }) => {
      state.user = {
        isAuthenticated: true,
        authData: payload
      };
      state.status = ApiResponseStatus.fulfilled;
      state.error = false;
    });

    builder.addCase(getToken.rejected, (state) => {
      state.user = {
        isAuthenticated: false,
        authData: {}
      };
      state.status = ApiResponseStatus.rejected;
      state.error = true;
    });

    // Employee ID by UPN cases
    builder.addCase(getEmployeeIdByUpn.pending, (state) => {
      state.employeeIdStatus = ApiResponseStatus.pending;
      state.employeeIdError = false;
    });

    builder.addCase(getEmployeeIdByUpn.fulfilled, (state, { payload }) => {
      state.employeeId = payload;
      state.employeeIdStatus = ApiResponseStatus.fulfilled;
      state.employeeIdError = false;
    });

    builder.addCase(getEmployeeIdByUpn.rejected, (state) => {
      state.employeeId = '';
      state.employeeIdStatus = ApiResponseStatus.rejected;
      state.employeeIdError = true;
    });
  }
});

const { actions, reducer } = userSlice;

export const { clearAuthState, setRefreshToken, clearEmployeeId } = actions;

export default reducer;
